import { useQuery } from 'react-query';
import * as Sentry from '@sentry/nextjs';
import { QUERY_KEYS } from '@libs/constants';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { TechnicalData } from '@models/data';
import type { ApiProductTechnicalDrawingsDataResponsePayload } from '@schemas/api-product-technical-drawings-data';
import { nextApiCall } from '@libs/nextApiCall';
import { nextApiCallErrorFn } from '@libs/nextApiCallErrorFn';

export const useTechnicalDrawingsDataQuery = (locale: string, technicalAttributes: string[]) => {
  const { id } = useProductPageStore(state => state.currentProduct);
  const unitSystem = useProductPageStore(state => state.unitSystem);

  return useQuery<TechnicalData[]>(
    [QUERY_KEYS.PRODUCT_TECHNICAL_DRAWINGS_DATA, id, locale, technicalAttributes],
    async () => {
      const response = await nextApiCall(
        'product-technical-drawings-data',
        {
          productId: id,
          locale,
          technicalAttributes,
          unitSystem,
        },
        'POST'
      );

      if (!response.ok) {
        const { status, statusText } = response;
        const error = new Error(`${status}: ${statusText}`);
        nextApiCallErrorFn(error, `Failed to fetch technical data for ${id}`);
        return null;
      }

      const { technicalData } = (await response.json()) as ApiProductTechnicalDrawingsDataResponsePayload;

      return technicalData;
    },
    {
      onError: error => {
        const errorObject = error as Record<string, string>;
        const message = errorObject?.message ? errorObject.message : JSON.stringify(error);

        Sentry.captureException(new Error(`Fetch product technical drawings data failed: ${message}`));
      },
    }
  );
};
